import { Maatregel, RijdtNietMaatregel, VertrekTijd } from "@/entities/vsm";
import { Form, FormFieldInput } from "@/shared/components";
import { uuid } from "@/shared/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { FormButtons, OpmerkingField, RichtingSwitch, SerieInput, StationSelect } from "./components.tsx";

const rijdtNietMaatregelSchema = z.discriminatedUnion("richting", [
    // ViceVersa variant - Van en Naar stations required
    z.object({
        type: z.literal("RijdtNiet"),
        treinserie: z.string().min(1, "Serie is verplicht"),
        richting: z.literal("ViceVersa"),
        vanStationCode: z.string().min(1, "Van station is verplicht"),
        naarStationCode: z.string().min(1, "Naar station is verplicht"),
        opmerking: z.string().optional(),
    }),
    // Heen variant - Van en Naar stations required + optional vertrekTijd
    z.object({
        type: z.literal("RijdtNiet"),
        treinserie: z.string().min(1, "Serie is verplicht"),
        richting: z.literal("Heen"),
        vanStationCode: z.string().min(1, "Van station is verplicht"),
        naarStationCode: z.string().min(1, "Naar station is verplicht"),
        vertrekTijd: VertrekTijd.zodType,
        opmerking: z.string().optional(),
    }),
]);

export type RijdtNietMaatregelFormData = z.infer<typeof rijdtNietMaatregelSchema>;

interface RijdtNietMaatregelFormProps {
    type: "create" | "update";
    onSubmit: (data: Maatregel) => void;
    onCancel: () => void;
    initialData?: RijdtNietMaatregel | undefined;
}

export function RijdtNietMaatregelForm({ onSubmit, onCancel, type, initialData }: Readonly<RijdtNietMaatregelFormProps>) {
    const form = useForm<RijdtNietMaatregelFormData>({
        resolver: zodResolver(rijdtNietMaatregelSchema),
        defaultValues: {
            type: "RijdtNiet",
            richting: initialData?.richting ?? "ViceVersa",
            treinserie: initialData?.treinserie ?? "",
            vanStationCode: initialData?.vanStationCode ?? "",
            naarStationCode: initialData?.naarStationCode ?? "",
            opmerking: initialData?.opmerking ?? "",
            vertrekTijd: VertrekTijd.toMinutes(initialData?.vertrekTijd),
        },
    });
    const { handleSubmit, watch, formState } = form;
    const isDirty = form.formState.isDirty;

    const richting = watch("richting");
    const treinserie = watch("treinserie");
    const isHeenSelected = richting === "Heen";

    const onSubmitForm = (data: RijdtNietMaatregelFormData) => {
        if (!isDirty) {
            onCancel();
            return;
        }
        let maatregel = convertToMaatregel(data);
        if (type === "update" && initialData) {
            maatregel = Maatregel.preserveMetadataForUpdate(maatregel, initialData.id, initialData.handmatigToegevoegd);
        }
        onSubmit(maatregel);
    };

    const normalizeVertrektijd = (value: string, setValue: (value: string) => void) => {
        if (/^\d$/.test(value)) {
            setValue(value.padStart(2, "0"));
        }
    };

    return (
        <div className="bg-emphasis-primary rounded-lg p-4">
            <Form {...form}>
                <form onSubmit={(e) => void handleSubmit(onSubmitForm)(e)} className="w-full min-w-0 space-y-2">
                    <div className="flex flex-row gap-4">
                        <SerieInput form={form} name="treinserie" />
                        <RichtingSwitch form={form} name="richting" />
                    </div>

                    <div className="flex w-full min-w-0 items-start gap-4">
                        {isHeenSelected && (
                            <FormFieldInput
                                form={form}
                                name="vertrekTijd"
                                placeholder=":min"
                                className="max-w-[80px]"
                                maxLength={2}
                                transformValueOnBlur={normalizeVertrektijd}
                            />
                        )}
                        <StationSelect form={form} name="vanStationCode" placeholder="Van" forTreinserie={treinserie} />
                        <StationSelect form={form} name="naarStationCode" placeholder="Naar" forTreinserie={treinserie} />
                    </div>

                    <OpmerkingField form={form} name="opmerking" />
                    <FormButtons
                        onCancel={onCancel}
                        isSubmitting={formState.isSubmitting}
                        submitLabel={type === "create" ? "Toevoegen" : "Opslaan"}
                    />
                </form>
            </Form>
        </div>
    );
}

function convertToMaatregel(data: RijdtNietMaatregelFormData): Maatregel {
    return {
        id: uuid(),
        type: data.type,
        treinserie: data.treinserie,
        richting: data.richting,
        vanStationCode: data.vanStationCode,
        naarStationCode: data.naarStationCode,
        vertrekTijd: data.richting === "Heen" && data.vertrekTijd ? VertrekTijd.toVertrekTijd(data.vertrekTijd) : undefined,
        opmerking: data.opmerking,
        handmatigToegevoegd: true,
        status: "Nieuw",
        opmerkingIsSpoorwebSuggestie: false,
    };
}
